export type PipelineOptionCapacityBasis = 'published' | 'estimated';
export type PipelineOptionStatus = 'operating' | 'project';

export interface PipelineOptionCapacity {
  systemKey?: string;
  capacityMmcfDay: number;
  capacityBasis: PipelineOptionCapacityBasis;
  status: PipelineOptionStatus;
  detail: string;
  sourceUrl?: string;
  sourceTitle?: string;
}

export interface PipelineOptionRow {
  id?: string;
  name?: string | null;
  system_name?: string | null;
  operator?: string | null;
  diameter_inches?: number | string | null;
  distance_miles?: number | string | null;
}

export interface PipelineOptionCapacityFact {
  systemKey?: string;
  capacityMmcfDay?: number | null;
  capacityBasis?: PipelineOptionCapacityBasis;
  status?: PipelineOptionStatus;
  detail?: string;
}

export interface PipelineOptionSummary {
  count: number;
  names: string[];
  totalMmcfDay: number | null;
  operatingMmcfDay: number | null;
  projectMmcfDay: number | null;
  hasEstimates: boolean;
  diameterLabel: string;
  distanceLabel: string;
  details: string[];
}

interface PipelineOptionSelector {
  system_key: string;
  name: string;
  operator: string;
  capacity?: PipelineOptionCapacity;
}

interface PipelineOptionsConfig {
  label: string;
  systems: unknown[];
}

type CapacityResolver = (pipeline: PipelineOptionRow) => PipelineOptionCapacityFact | null | undefined;

function normalized(value: unknown): string {
  return typeof value === 'string'
    ? value.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim()
    : '';
}

function finitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function compactNumber(value: number): string {
  return Number(value.toFixed(2)).toString();
}

export function formatOptionCapacity(mmcf: number): string {
  if (!Number.isFinite(mmcf) || mmcf < 0) return 'Unconfirmed';
  if (mmcf < 1000) return `${compactNumber(mmcf)} MMcf/d`;
  return `${compactNumber(mmcf / 1000)} Bcf/d`;
}

function finiteAttribute(value: unknown, allowZero = false): number | null {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && (allowZero ? number >= 0 : number > 0) ? number : null;
}

function geometryLabels(rows: PipelineOptionRow[], expectedCount: number): Pick<PipelineOptionSummary, 'diameterLabel' | 'distanceLabel'> {
  if (expectedCount === 0 || rows.length !== expectedCount) {
    return { diameterLabel: 'Unconfirmed', distanceLabel: 'Unconfirmed' };
  }
  const diameters = rows.map(row => finiteAttribute(row.diameter_inches)).filter((value): value is number => value != null);
  const distances = rows.map(row => finiteAttribute(row.distance_miles, true)).filter((value): value is number => value != null);

  const diameterLabel = diameters.length !== rows.length ? 'Unconfirmed' : (() => {
    const min = Math.min(...diameters);
    const max = Math.max(...diameters);
    return min === max ? `${compactNumber(min)}″` : `${compactNumber(min)}–${compactNumber(max)}″`;
  })();

  const distanceLabel = distances.length !== rows.length ? 'Unconfirmed' : (() => {
    const min = Math.min(...distances).toFixed(2);
    const max = Math.max(...distances).toFixed(2);
    return min === max ? `${min} mi` : `${min}–${max} mi`;
  })();

  return { diameterLabel, distanceLabel };
}

function unconfirmedSummary(
  count: number,
  names: string[],
  details: string[],
  rows: PipelineOptionRow[] = [],
  hasEstimates = false,
): PipelineOptionSummary {
  return {
    count,
    names,
    totalMmcfDay: null,
    operatingMmcfDay: null,
    projectMmcfDay: null,
    hasEstimates,
    ...geometryLabels(rows, count),
    details,
  };
}

function isOptionsConfig(value: unknown): value is PipelineOptionsConfig {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.label === 'string' && Array.isArray(candidate.systems);
}

function validSelector(value: unknown): value is PipelineOptionSelector {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return normalized(candidate.system_key) !== ''
    && normalized(candidate.name) !== ''
    && normalized(candidate.operator) !== '';
}

function validCapacity(value: PipelineOptionCapacityFact | null | undefined): value is PipelineOptionCapacity {
  return !!value
    && finitePositive(value.capacityMmcfDay)
    && (value.capacityBasis === 'published' || value.capacityBasis === 'estimated')
    && (value.status === 'operating' || value.status === 'project')
    && typeof value.detail === 'string'
    && value.detail.trim() !== '';
}

/**
 * Summarize only curator-selected, distinct pipeline systems. Nearby rows and their
 * general capacity fields never enter the arithmetic implicitly.
 */
export function selectedPipelineCapacitySummary(
  pipelines: PipelineOptionRow[] | null | undefined,
  config: unknown,
  resolver?: CapacityResolver,
): PipelineOptionSummary | null {
  if (config == null) return null;
  if (!isOptionsConfig(config)) {
    return unconfirmedSummary(0, [], ['Capacity confirmation required: the selected pipeline information is incomplete.']);
  }

  const systems = config.systems;
  const names = systems.map(system => validSelector(system) ? system.name : 'Unknown');
  if (systems.length === 0) {
    return unconfirmedSummary(0, [], ['Capacity confirmation required: no pipeline options were selected.']);
  }
  if (!systems.every(validSelector)) {
    return unconfirmedSummary(systems.length, names, ['Capacity confirmation required: a selected pipeline is incomplete.']);
  }
  const selectedSystems = systems as PipelineOptionSelector[];

  const seenKeys = new Set<string>();
  const seenSelectors = new Set<string>();
  for (const system of selectedSystems) {
    const key = normalized(system.system_key);
    const identity = `${normalized(system.name)}|${normalized(system.operator)}`;
    if (seenKeys.has(key)) {
      return unconfirmedSummary(systems.length, names, [`Duplicate selected pipeline key: ${system.system_key}.`]);
    }
    if (seenSelectors.has(identity)) {
      return unconfirmedSummary(systems.length, names, [`Duplicate pipeline selector: ${system.name} / ${system.operator}.`]);
    }
    seenKeys.add(key);
    seenSelectors.add(identity);
  }

  const rows = Array.isArray(pipelines) ? pipelines : [];
  const matched: PipelineOptionRow[] = [];
  const matchedIndexes = new Set<number>();
  for (const system of selectedSystems) {
    const targetName = normalized(system.name);
    const targetOperator = normalized(system.operator);
    const matches = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => normalized(row.operator) === targetOperator
        && (normalized(row.system_name) === targetName || normalized(row.name) === targetName));

    if (matches.length === 0) {
      return unconfirmedSummary(systems.length, names, [`Missing selected pipeline: ${system.name} / ${system.operator}.`], matched);
    }
    if (matches.length > 1) {
      return unconfirmedSummary(systems.length, names, [`Ambiguous selected pipeline: ${system.name} / ${system.operator}.`], matched);
    }
    if (matchedIndexes.has(matches[0].index)) {
      return unconfirmedSummary(systems.length, names, [`Ambiguous selectors resolve to the same pipeline: ${system.name}.`], matched);
    }
    matchedIndexes.add(matches[0].index);
    matched.push(matches[0].row);
  }

  const capacities: PipelineOptionCapacity[] = [];
  const errors: string[] = [];
  for (let index = 0; index < selectedSystems.length; index += 1) {
    const system = selectedSystems[index];
    let capacity: PipelineOptionCapacityFact | null | undefined = system.capacity;
    if (capacity == null && resolver) {
      try {
        capacity = resolver(matched[index]);
      } catch {
        capacity = null;
      }
    }
    if (!validCapacity(capacity)) {
      errors.push(`Unconfirmed capacity for selected pipeline: ${system.name}.`);
      continue;
    }
    if (capacity.systemKey && normalized(capacity.systemKey) !== normalized(system.system_key)) {
      errors.push(`Capacity confirmation required for selected pipeline: ${system.name}.`);
      continue;
    }
    capacities.push(capacity);
  }

  const hasEstimates = capacities.some(capacity => capacity.capacityBasis === 'estimated');
  if (errors.length > 0) {
    return unconfirmedSummary(systems.length, names, errors, matched, hasEstimates);
  }

  const operating = capacities.filter(capacity => capacity.status === 'operating');
  const projects = capacities.filter(capacity => capacity.status === 'project');
  return {
    count: selectedSystems.length,
    names,
    totalMmcfDay: capacities.reduce((sum, capacity) => sum + capacity.capacityMmcfDay, 0),
    operatingMmcfDay: operating.reduce((sum, capacity) => sum + capacity.capacityMmcfDay, 0),
    projectMmcfDay: projects.reduce((sum, capacity) => sum + capacity.capacityMmcfDay, 0),
    hasEstimates,
    ...geometryLabels(matched, selectedSystems.length),
    details: capacities.map(capacity => capacity.detail),
  };
}
